import { ComponentType, lazy } from 'react';
import {
  isChunkLoadError,
  reloadOnceForStaleRelease,
} from '@components/RouteErrorPage';

type LazyModule<T extends ComponentType<any>> = { default: T };

const DEFAULT_RETRY_DELAY_MS = 1000;

let pendingLazyImports = 0;

/**
 * True while a lazy route import is in flight. handlePreloadError leaves
 * those failures to importWithRetry (retry once, reload once) instead of
 * reloading on its own.
 */
export function isLazyRouteImportPending(): boolean {
  return pendingLazyImports > 0;
}

function wait(ms: number) {
  return new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });
}

function requireDefault<T extends ComponentType<any>>(
  module: LazyModule<T> | undefined
): LazyModule<T> {
  if (!module || module.default === undefined) {
    // Same wording as the browser's own crash, so the route error page
    // recognises it as a stale release (isChunkLoadError) and reloads.
    throw new TypeError(
      "Cannot read properties of undefined (reading 'default'): the lazy module has no default export, likely a chunk from a previous release"
    );
  }
  return module;
}

/**
 * Run a dynamic import for a lazy route (ZLAYT-1485). A failed import is
 * retried once after a short delay (best effort: a browser may answer the
 * second import of the same URL from its module map); if the chunk is still
 * missing, the page reloads once (same guard as RouteErrorPage) to pick up
 * the new release. The error is rethrown either way so the route error page
 * can show Reload.
 */
export async function importWithRetry<T extends ComponentType<any>>(
  importer: () => Promise<LazyModule<T>>,
  retryDelayMs = DEFAULT_RETRY_DELAY_MS
): Promise<LazyModule<T>> {
  pendingLazyImports += 1;
  try {
    let module: LazyModule<T>;
    try {
      module = await importer();
    } catch {
      await wait(retryDelayMs);
      try {
        module = await importer();
      } catch (error) {
        if (isChunkLoadError(error)) reloadOnceForStaleRelease();
        throw error;
      }
    }
    return requireDefault(module);
  } finally {
    pendingLazyImports -= 1;
  }
}

/** React.lazy with one retry and a once-per-tab reload for stale chunks. */
export default function lazyWithRetry<T extends ComponentType<any>>(
  importer: () => Promise<LazyModule<T>>,
  retryDelayMs = DEFAULT_RETRY_DELAY_MS
) {
  return lazy(() => importWithRetry(importer, retryDelayMs));
}
